import { z } from 'zod';
import type { Brand } from './brand';

/**
 * Currencies and experience are **integers stored as `bigint`**.
 *
 * Floating point is forbidden for anything the ledger touches: `0.1 + 0.2` and
 * silent precision loss past 2^53 are both unacceptable when the value is real
 * money or a tradeable asset. See `docs/SECURITY.md`.
 *
 * ## Capacity
 *
 * A long-running idle game outgrows PostgreSQL `bigint` (signed 64-bit, ~19
 * digits). The ledger column is therefore `numeric(40, 0)` and the application
 * enforces the *same* bound before a value ever reaches the database:
 * magnitude strictly below 10^40. A request outside that range is rejected, not
 * clamped and not truncated.
 *
 * Three representations exist and conversion between them is always explicit:
 *
 * | Layer     | Representation                          |
 * | --------- | --------------------------------------- |
 * | Database  | `numeric(40, 0)`                        |
 * | Transport | decimal **string** (JSON has no bigint) |
 * | Domain    | `bigint` behind a nominal brand         |
 */

/** Maximum number of decimal digits, matching `numeric(40, 0)` in PostgreSQL. */
export const CURRENCY_MAX_DIGITS = 40;

/** Largest representable magnitude: 10^40 - 1, for both signs. */
export const CURRENCY_MAX_MAGNITUDE = 10n ** BigInt(CURRENCY_MAX_DIGITS) - 1n;

/**
 * A **balance**: never negative. Gold, Cash and XP are all balances.
 */
export type GoldAmount = Brand<bigint, 'GoldAmount'>;
export type CashAmount = Brand<bigint, 'CashAmount'>;
export type XpAmount = Brand<bigint, 'XpAmount'>;

/**
 * A **signed ledger delta**: a single append-only movement, which may be
 * negative (a spend, a fee, a compensating correction). Deliberately a
 * different type from a balance so a delta cannot be stored as a balance, or
 * vice versa, without an explicit conversion.
 */
export type LedgerDelta = Brand<bigint, 'LedgerDelta'>;

export const CURRENCY_KINDS = ['gold', 'cash'] as const;
export type CurrencyKind = (typeof CURRENCY_KINDS)[number];
export const currencyKindSchema = z.enum(CURRENCY_KINDS);

/** Non-negative, no leading zeros, at most `CURRENCY_MAX_DIGITS` digits. */
const BALANCE_PATTERN = /^(0|[1-9]\d{0,39})$/;

/** Signed variant. `-0` is rejected: there is exactly one representation of 0. */
const DELTA_PATTERN = /^(0|-?[1-9]\d{0,39})$/;

function assertWithinCapacity(value: bigint, label: string): void {
  if (value > CURRENCY_MAX_MAGNITUDE || value < -CURRENCY_MAX_MAGNITUDE) {
    throw new RangeError(
      `${label} exceeds the ${CURRENCY_MAX_DIGITS}-digit currency capacity, received ${value}`,
    );
  }
}

function assertBalance(value: bigint, label: string): bigint {
  if (value < 0n) {
    throw new RangeError(`${label} must not be negative, received ${value}`);
  }
  assertWithinCapacity(value, label);
  return value;
}

export function goldAmount(value: bigint): GoldAmount {
  return assertBalance(value, 'GoldAmount') as GoldAmount;
}

export function cashAmount(value: bigint): CashAmount {
  return assertBalance(value, 'CashAmount') as CashAmount;
}

export function xpAmount(value: bigint): XpAmount {
  return assertBalance(value, 'XpAmount') as XpAmount;
}

/** Signed movement. Negative is legal here and only here. */
export function ledgerDelta(value: bigint): LedgerDelta {
  assertWithinCapacity(value, 'LedgerDelta');
  return value as LedgerDelta;
}

/**
 * Canonical wire format for a **balance**.
 *
 * Always a string. Never a JSON number — a JSON number would be parsed as a
 * double by every client and by most tooling in between.
 */
export const balanceAmountStringSchema = z
  .string()
  .regex(
    BALANCE_PATTERN,
    `balance must be a non-negative integer of at most ${CURRENCY_MAX_DIGITS} digits`,
  );

/** Canonical wire format for a **signed ledger delta**. */
export const ledgerDeltaStringSchema = z
  .string()
  .regex(
    DELTA_PATTERN,
    `ledger delta must be a signed integer of at most ${CURRENCY_MAX_DIGITS} digits`,
  );

export const goldAmountSchema = balanceAmountStringSchema.transform((value) =>
  goldAmount(BigInt(value)),
);
export const cashAmountSchema = balanceAmountStringSchema.transform((value) =>
  cashAmount(BigInt(value)),
);
export const xpAmountSchema = balanceAmountStringSchema.transform((value) =>
  xpAmount(BigInt(value)),
);
export const ledgerDeltaSchema = ledgerDeltaStringSchema.transform((value) =>
  ledgerDelta(BigInt(value)),
);

/** Serializes any branded amount back to the wire format. */
export function serializeAmount(value: GoldAmount | CashAmount | XpAmount | LedgerDelta): string {
  return (value as bigint).toString(10);
}

/** True when a raw `bigint` fits the ledger's declared capacity. */
export function isWithinCurrencyCapacity(value: bigint): boolean {
  return value <= CURRENCY_MAX_MAGNITUDE && value >= -CURRENCY_MAX_MAGNITUDE;
}
